import {
  Alert,
  AlertIcon,
  Button,
  Card,
  CardBody,
  Container,
  Divider,
  Heading,
  Image,
  Skeleton,
  SkeletonText,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import {
  AircraftType,
  CargoType,
  DimensionUnit,
  QuoteStage,
  QuoteStatus,
  RequestStatus,
  WeightUnit,
} from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { ETT_SORT_ORDER, getPrettyCargoType, join, prettyDate, prettyNumber } from '@pelicargo/utils';
import { uniq, uniqBy } from 'lodash';
import { useEffect, useMemo } from 'react';
import { HiChevronLeft } from 'react-icons/hi';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { LinkedRequestModal } from '../../components/LinkedRequestModal';
import { Page } from '../../components/Page';
import { QuoteFilters, QuoteListFilters } from '../../components/QuoteFilters';
import { QuoteListingModal } from '../../components/QuoteListingModal';
import { QuoteListItem } from '../../components/QuoteListItem';
import { ShareButton } from '../../components/ShareButton';
import { SupportButton } from '../../components/SupportButton';
import { SelectedQuoteVariantProvider } from '../../context/SelectedQuoteVariantContext';
import { cheapestQuoteSort, useQuotes } from '../../hooks/quotes/useQuotes';
import { useRequest } from '../../hooks/request/useRequest';
import { useRequestPickupOrigin } from '../../hooks/request/useRequestPickupOrigin';
import { useFilters } from '../../hooks/useFilters';
import { API } from '../../utils/apiTypes';
import { CONTAINER_2XL } from '../../utils/constants';

type QuoteWithTotalCost = API.QuoteWithTotalCost;
type QuoteStatusType = API.QuoteWithTotalCost['status'];
type Params = {
  requestId?: string;
};

const fastestQuoteSort = (quotes: QuoteWithTotalCost[]) => {
  if (!quotes.length) return [];

  const priorityTypes: QuoteStatusType[] = [
    QuoteStatus.Confirmed,
    QuoteStatus.BookingPending,
    QuoteStatus.BookingRequested,
    QuoteStatus.InProgress,
  ];
  const sortedQuotes = quotes
    .filter((quote) => priorityTypes.includes(quote.status))
    .map((quote) => {
      let earliestDelivery = Number(quote.estimated_transit_time[0]);
      if (isNaN(earliestDelivery)) earliestDelivery = Infinity;

      quote.earliestDelivery = earliestDelivery;

      return quote;
    })
    .sort((a, b) => (a.earliestDelivery || 0) - (b.earliestDelivery || 0));

  const expiredOrPendingQuotes = quotes.filter((quote) => !priorityTypes.includes(quote.status));
  return [...sortedQuotes, ...expiredOrPendingQuotes];
};

const sortQuotes = (quotes: API.QuoteWithTotalCost[], filters: QuoteFilters) => {
  // quotes provided by useQuotes is always sorted by cheapest
  if (filters.sort === 'Cheapest') return cheapestQuoteSort(quotes);
  else if (filters.sort === 'Fastest') return fastestQuoteSort(quotes);
};

const filterQuotesByProvider = (quotes: API.QuoteWithTotalCost[], filters: QuoteFilters) => {
  if (!filters.providers) return quotes;
  return quotes.filter((quote) => filters.providers.includes(quote.provider_id));
};

const filterQuotesByGroupedProvider = (quotes: API.QuoteWithTotalCost[], filter: QuoteFilters) => {
  if (!filter.groupByProvider) return quotes;

  const groupedQuotes = quotes?.reduce(
    (acc, quote) => {
      const key = `${quote.provider_id}-${quote.origin_airport_id}-${quote.destination_airport_id}`;
      acc[key] = acc[key] ?? [];
      acc[key].push(quote);
      return acc;
    },
    {} as Record<string, typeof quotes>,
  );

  const bestQuotePerProvider = Object.values(groupedQuotes || {}).map((group) => {
    // cost, ETT, number of stops, earliest departure
    const sortedByProvider = group.sort((a, b) => {
      const cheapestVariantA = a.quote_variants?.reduce((min, variant) => {
        const variantCost = variant?.line_items?.reduce((sum, item) => sum + item.cost, 0) ?? Infinity;
        return Math.min(min, variantCost);
      }, Infinity);

      const cheapestVariantB = b.quote_variants?.reduce((min, variant) => {
        const variantCost = variant?.line_items?.reduce((sum, item) => sum + item.cost, 0) ?? Infinity;
        return Math.min(min, variantCost);
      }, Infinity);

      const totalCostDiff = cheapestVariantA - cheapestVariantB;
      if (totalCostDiff !== 0) return totalCostDiff;

      const transitTimeDiff = ETT_SORT_ORDER[a.estimated_transit_time] - ETT_SORT_ORDER[b.estimated_transit_time];
      if (transitTimeDiff !== 0) return transitTimeDiff;

      const flightPathLengthDiff = a.flight_path.length - b.flight_path.length;
      if (flightPathLengthDiff !== 0) return flightPathLengthDiff;

      const earliestDepartureA = new Date(a?.flight_path[0]?.departs_at);
      const earliestDepartureB = new Date(b?.flight_path[0]?.departs_at);

      return earliestDepartureA.getTime() - earliestDepartureB.getTime();
    });

    return sortedByProvider?.[0];
  });

  return bestQuotePerProvider;
};

const filterQuotesByAirport = (quotes: API.QuoteWithTotalCost[], filters: QuoteFilters) => {
  const hasOriginFilter = !!filters.origins?.length;
  const hasDestinationFilter = !!filters.destinations?.length;

  // If no filters defined, return all quotes
  if (!hasOriginFilter && !hasDestinationFilter) return quotes;

  return quotes.filter((quote) => {
    const matchesOrigin = filters.origins?.includes(quote.origin_airport_id);
    const matchesDestination = filters.destinations?.includes(quote.destination_airport_id);

    // If it has both origin and destination filters defined, ensure it matches both
    if (hasOriginFilter && hasDestinationFilter) return matchesOrigin && matchesDestination;

    // If only origin, check only origin
    if (hasOriginFilter) return matchesOrigin;

    // If only destination, check only destination
    if (hasDestinationFilter) return matchesDestination;
  });
};

const filterQuotesByStops = (quotes: API.QuoteWithTotalCost[], filters: QuoteFilters) => {
  switch (filters.stops) {
    case 'any':
      return quotes;
    case 'nonstop':
      return quotes.filter((quote) => quote.flight_path.length === 1);
    default:
      break;
  }
};

const filterQuotesByAircraft = (quotes: API.QuoteWithTotalCost[], filters: QuoteFilters) => {
  switch (filters.aircraft) {
    case 'any':
      return quotes;
    case AircraftType.RFS:
      return quotes.filter((quote) => quote?.flight_path?.some((flight) => flight.aircraft_type === AircraftType.RFS));
    default:
      break;
  }
};

const filterQuotesByServiceType = (quotes: API.QuoteWithTotalCost[], filters: QuoteFilters) => {
  if (!filters.serviceType) return quotes;
  return quotes?.filter(
    ({ quote_variants: qvs }) =>
      // Do not filter if there are no QVs
      !qvs?.length || qvs?.some((variant) => variant.type === filters?.serviceType),
  );
};

export const QuoteListing = () => {
  const navigate = useNavigate();
  const params: Params = useParams();
  const { state } = useLocation();
  const requestDetailsModal = useDisclosure();

  const [request, isLoading, error] = useRequest(Number(params?.requestId), state?.request);
  const { quotes, isLoading: isQuotesLoading, error: quotesError } = useQuotes(Number(params?.requestId));
  const { filters, handleFilterChange } = useFilters<QuoteFilters>({
    providers: [],
    origins: [],
    destinations: [],
    stops: 'any',
    aircraft: 'any',
    serviceType: null,
    groupByProvider: true,
    sort: 'Cheapest',
  });
  const zipOrigin = useRequestPickupOrigin(request);
  const requestPickupOrigin = zipOrigin;

  const origins = useMemo(() => {
    return uniq(
      quotes?.map((quote) => quote?.origin_airport_id) ||
        request?.origin_airports?.map((airport) => airport.airport_as_origin_id) ||
        [],
    ).filter(Boolean);
  }, [quotes, request?.origin_airports]);

  const destinations = useMemo(
    () =>
      uniq(
        quotes?.map((quote) => quote?.destination_airport_id) ||
          request?.destination_airports?.map((airport) => airport.airport_as_destination_id) ||
          [],
      ).filter(Boolean),
    [quotes, request?.destination_airports],
  );

  const providers = useMemo(() => {
    const allProviders =
      quotes?.map((quote) => ({
        label: quote?.provider?.name,
        value: quote?.provider?.id,
      })) ?? [];

    return uniqBy(
      allProviders.filter((provider) => !!provider.value),
      'value',
    );
  }, [quotes]);

  useEffect(() => {
    handleFilterChange('providers')(providers?.map((provider) => provider.value));
    handleFilterChange('origins')(origins);
    handleFilterChange('destinations')(destinations);
  }, [providers, origins, destinations, handleFilterChange]);

  const filteredQuotes = useMemo(() => {
    let filteredQuotes = filterQuotesByProvider(quotes, filters);
    filteredQuotes = filterQuotesByAirport(filteredQuotes, filters);
    filteredQuotes = filterQuotesByStops(filteredQuotes, filters);
    filteredQuotes = filterQuotesByAircraft(filteredQuotes, filters);
    filteredQuotes = filterQuotesByServiceType(filteredQuotes, filters);
    filteredQuotes = filterQuotesByGroupedProvider(filteredQuotes, filters);
    return sortQuotes(filteredQuotes, filters);
  }, [quotes, filters]);

  const stage2Quotes = useMemo(() => {
    return filteredQuotes?.filter(({ status, stage }) => {
      const hasStatus = !!status;
      const hasStatusThatIsNotReadyForDetails = (
        [QuoteStatus.Pending, QuoteStatus.ReadyForQC, QuoteStatus.SentToCarrier] as QuoteStatus[]
      ).includes(status as QuoteStatus);

      return hasStatus && !hasStatusThatIsNotReadyForDetails && stage === QuoteStage.TWO;
    });
  }, [filteredQuotes]);

  const stage1Quotes = useMemo(() => {
    return filteredQuotes?.filter((quote) => quote.stage === QuoteStage.ONE);
  }, [filteredQuotes]);

  const stage0Quotes = useMemo(() => {
    const providersInOtherStages = filteredQuotes
      ?.filter((quote) => quote.stage !== QuoteStage.ZERO)
      ?.map((node) => node?.provider_id);

    const uniqueStageZeroAirlines = filteredQuotes?.filter((quote) => {
      const isStageZero = quote.stage === QuoteStage.ZERO;
      const providerInAnotherStage = providersInOtherStages?.includes(quote.provider_id);

      return isStageZero && !providerInAnotherStage;
    });

    return uniqBy(uniqueStageZeroAirlines, 'provider_id');
  }, [filteredQuotes]);

  const renderQuote = (quote) => {
    return (
      <SelectedQuoteVariantProvider key={quote?.id}>
        <QuoteListItem
          quote={quote}
          selectedCargoType={filters.serviceType}
          requestPickupOrigin={requestPickupOrigin}
          requestChargeableWeight={request?.chargeable_weight}
        />
      </SelectedQuoteVariantProvider>
    );
  };

  const isRequestOpen = useMemo(() => {
    return (
      [RequestStatus.New, RequestStatus.QuotingInProgress, RequestStatus.QuotingPending] as RequestStatus[]
    ).includes(request?.status);
  }, [request?.status]);

  if (error || quotesError?.message) {
    return (
      <Page title="Quote" flex="1">
        <Container w="full" maxW="container.xl" py={{ base: '8', lg: '16' }}>
          <Alert status="error">
            <AlertIcon />
            {error || quotesError?.message}
          </Alert>
        </Container>
      </Page>
    );
  }

  const pieces = request?.cargo?.reduce((sum, cargo) => sum + cargo.quantity, 0);
  const requestIdFromParam = Number(params?.requestId);

  return (
    <Page title="Quote" flex="1" pb="20">
      <Container w="full" maxW={CONTAINER_2XL} py={{ base: '8', lg: '10' }} px={{ base: '4', lg: '12' }}>
        <Stack spacing="6">
          <Stack direction="row" align="center" justify="space-between">
            <Button variant="outline" size="sm" leftIcon={<HiChevronLeft />} onClick={() => navigate('/requests')}>
              Requests
            </Button>
            <Stack direction="row" spacing="2">
              <SupportButton requestId={requestIdFromParam} type="Update" />
              <ShareButton requestId={requestIdFromParam} type="Update" />
            </Stack>
          </Stack>
          <Stack direction="row" spacing={7}>
            <Stack spacing={0}>
              <Text textStyle="captionSmallSemibold" color="gray.600">
                REQUEST#
              </Text>
              <SkeletonText isLoaded={!isLoading} noOfLines={1}>
                <Heading variant="h4">{`R${request?.id}`}</Heading>
              </SkeletonText>
            </Stack>
            <Show if={!!request?.customer_ref}>
              <Stack spacing={0}>
                <Text textStyle="captionSmallSemibold" color="gray.600">
                  YOUR REFERENCE#
                </Text>
                <Heading variant="h4">{request?.customer_ref}</Heading>
              </Stack>
            </Show>
          </Stack>
          <Skeleton borderRadius="md" isLoaded={!isLoading}>
            <Card px="4" py="3">
              <CardBody p="0">
                <Stack spacing="0">
                  <Stack direction="row" align="center" justify="space-between" wrap="wrap">
                    <Stack direction="row" align="center" wrap="wrap" spacing="4">
                      <Text textStyle="bodySmallRegular" color="text.paragraph">
                        {zipOrigin ?? join(origins)} → {join(destinations)}
                      </Text>
                      <Divider orientation="vertical" borderColor="border.light" borderWidth="1px" h="6" />
                      <Text textStyle="bodySmallRegular">{getPrettyCargoType(request?.cargo_type as CargoType)}</Text>
                      <Divider orientation="vertical" borderColor="border.light" borderWidth="1px" h="6" />
                      <Text textStyle="bodySmallRegular">{pieces === 1 ? `${pieces} Piece` : `${pieces} Pieces`} </Text>
                      <Divider orientation="vertical" borderColor="border.light" borderWidth="1px" h="6" />
                      <Text textStyle="bodySmallRegular">{`Ch. Weight ${prettyNumber(request?.chargeable_weight)} kg`}</Text>
                      <Show if={request?.is_known_shipper}>
                        <Divider orientation="vertical" borderColor="border.light" borderWidth="1px" h="6" />
                        <Text textStyle="bodySmallRegular">Known Shipper</Text>
                      </Show>
                    </Stack>
                    <Button size="sm" onClick={requestDetailsModal.onOpen}>
                      View Details
                    </Button>
                  </Stack>
                </Stack>
              </CardBody>
            </Card>
          </Skeleton>
          <QuoteListFilters
            filters={filters}
            origins={origins}
            destinations={destinations}
            providers={providers}
            handleFilterChange={handleFilterChange}
          />
          <Divider />

          <Show if={isRequestOpen}>
            <Alert variant="top-accent" status="info" justifyContent="center">
              <AlertIcon />
              Competitive quotes stream in live over time! You will receive an email when quoting is complete.
            </Alert>
          </Show>

          <Stack spacing="8">
            <Show if={!quotes?.length && isQuotesLoading}>
              <Stack spacing="4">
                {Array.from({ length: 8 }).map((_, index) => (
                  <Card key={index}>
                    <CardBody>
                      <Stack direction="row" align="center" justify="space-between">
                        <SkeletonText isLoaded={false} noOfLines={1} height="8">
                          <Text>Fake Copy</Text>
                        </SkeletonText>
                        <SkeletonText isLoaded={false} noOfLines={1} height="8">
                          <Text>Fake Copy</Text>
                        </SkeletonText>
                        <SkeletonText isLoaded={false} noOfLines={1} height="8">
                          <Text>Fake Copy</Text>
                        </SkeletonText>
                      </Stack>
                    </CardBody>
                  </Card>
                ))}
              </Stack>
            </Show>
            <Show if={!quotes?.length && !isQuotesLoading}>
              <Stack spacing="8" w="full" align="center" py={10}>
                <Text>Your request is being worked on, please check back soon!</Text>
              </Stack>
            </Show>
            <Stack spacing="8">
              <Show if={!!stage2Quotes.length}>
                <Stack spacing="2">{stage2Quotes?.map(renderQuote)}</Stack>
              </Show>
              <Show if={!!stage1Quotes.length}>
                <Stack>
                  <Heading as="h6" variant="h6">
                    Quoting
                  </Heading>
                  <Stack spacing="2">{stage1Quotes?.map(renderQuote)}</Stack>
                </Stack>
              </Show>
            </Stack>
            <Show if={!!stage0Quotes.length}>
              <Stack direction="row" align="center" justify="center" wrap="wrap" spacing="4">
                {stage0Quotes?.map((quote) => (
                  <Tooltip key={quote?.id} label={quote?.provider?.name}>
                    <Image src={quote?.provider?.logo_uri} h="52px" w="52px" />
                  </Tooltip>
                ))}
              </Stack>
            </Show>
          </Stack>
        </Stack>
      </Container>

      <QuoteListingModal
        isOpen={requestDetailsModal.isOpen}
        onClose={requestDetailsModal.onClose}
        origin={zipOrigin ?? join(origins)}
        commodity={request?.commodity}
        destination={join(destinations)}
        cargo={request?.cargo}
        dropOffBy={prettyDate(request?.dropoff_by)}
        cargoType={request?.cargo_type}
        grossWeight={request?.gross_weight}
        volumetricWeight={request?.volumetric_weight}
        isKnownShipper={request?.is_known_shipper}
        dimensionUnit={request?.original_dimension_unit as DimensionUnit}
        weightUnit={request?.original_weight_unit as WeightUnit}
        request={request}
      />
      <LinkedRequestModal request={request} />
    </Page>
  );
};
